import React from "react";
import * as Icons from "lucide-react";
import { useData } from "@/context/DataContext";
import { Campo, Opcion, Pregunta, inputCls } from "./ui";
import { MensajeCorreo, textoMensaje } from "./MensajeCorreo";
import { parseImporte } from "@/lib/gastos";
import { importeTxt } from "@/lib/recordatorios";
import { Relacionar } from "@/components/asuntos/RecordatorioPuntual";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validarPaso3 = (f) => {
  const ps = f.personas.map((p) => p.trim());
  if (f.para === "otra" && !ps[0]) return { campo: "persona-0", txt: "Escribe el nombre de la persona." };
  if (f.para === "varias") {
    const i = ps.findIndex((p) => !p);
    if (i >= 0) return { campo: `persona-${i}`, txt: "Escribe el nombre o quita esta fila." };
    if (ps.length < 2) return { campo: "personas", txt: "Añade al menos dos personas." };
  }
  if (f.modo !== "correo") return null;
  if (!f.destinatarios.length) return { campo: "dest", txt: "Indica a quién quieres escribir." };
  for (let i = 0; i < f.destinatarios.length; i += 1) {
    const d = f.destinatarios[i];
    if (!d.nombre.trim()) return { campo: `dest-nombre-${i}`, txt: "Escribe el nombre de la persona o entidad." };
    if (!EMAIL_RE.test(d.email.trim())) return { campo: `dest-email-${i}`, txt: "Escribe un correo electrónico válido, por ejemplo ana@gmail.com." };
    if (f.importeDistinto && textoMensaje(f).includes("[importe]") && !(Number(d.importe) >= 0 && d.importe !== "")) return { campo: `dest-importe-${i}`, txt: "Indica el importe de esta persona." };
  }
  if (!f.importeDistinto && textoMensaje(f).includes("[importe]") && f.importe === "") return { campo: "importe-general", txt: "El mensaje incluye el importe: indícalo en el paso 1 o marca «Cada persona paga un importe distinto»." };
  return null;
};

const Personas = ({ f, set, err }) => (
  <div className="space-y-2">
    {f.personas.map((p, i) => (
      <Campo key={i} label={f.para === "varias" ? `Persona ${i + 1}` : "Nombre de la persona"} error={err(`persona-${i}`)} testid={`rp-campo-persona-${i}`}>
        <div className="flex gap-2">
          <input data-testid={`rp-persona-${i}`} className={inputCls} value={p} placeholder="Nombre" onChange={(e) => set({ personas: f.personas.map((x, j) => (j === i ? e.target.value : x)) })} />
          {f.para === "varias" && f.personas.length > 2 && (
            <button type="button" aria-label="Quitar persona" data-testid={`rp-persona-quitar-${i}`} onClick={() => set({ personas: f.personas.filter((_, j) => j !== i) })} className="min-w-[52px] rounded-xl border border-[#E5DFD5] flex items-center justify-center text-[#6E7A74]"><Icons.Trash2 size={18} /></button>
          )}
        </div>
      </Campo>
    ))}
    {f.para === "varias" && <button type="button" data-testid="rp-persona-anadir" onClick={() => set({ personas: [...f.personas, ""] })} className="min-h-[48px] text-base font-semibold text-[#3A6B56]">+ Añadir otra persona</button>}
    {err("personas") && <p className="text-sm font-semibold text-[#B91C1C]" role="alert">{err("personas")}</p>}
  </div>
);

const Destinatarios = ({ f, set, err }) => {
  const { contactos = [] } = useData();
  const conEmail = contactos.filter((c) => c.email);
  const setD = (i, k, v) => set({ destinatarios: f.destinatarios.map((d, j) => (j === i ? { ...d, [k]: v } : d)) });
  const anadir = (d) => set({ destinatarios: [...f.destinatarios, d] });
  const [repetido, setRepetido] = React.useState(false);
  const emails = f.destinatarios.map((d) => d.email.trim().toLowerCase()).filter(Boolean);
  const hayDuplicado = repetido || emails.length !== new Set(emails).size;
  const clave = JSON.stringify(f.destinatarios);
  // Cualquier cambio en los destinatarios retira el aviso de un intento anterior; solo queda si sigue habiendo duplicidad.
  React.useEffect(() => { setRepetido(false); }, [clave]);
  const elegirContacto = (c) => {
    const ya = f.destinatarios.some((d) => d.email.trim().toLowerCase() === c.email.trim().toLowerCase());
    setRepetido(ya);
    if (!ya) set({ destinatarios: [...f.destinatarios.filter((d) => d.nombre.trim() || d.email.trim()), { nombre: c.nombre, email: c.email, importe: "" }] });
  };
  return (
    <div className="space-y-4" data-testid="rp-destinatarios">
      <Pregunta>¿A quién quieres escribir?</Pregunta>
      <p className="text-base text-[#4A5550]">Este es el destinatario del correo. El recordatorio seguirá apareciendo en tu cuenta de AL DÍA.</p>
      {f.destinatarios.map((d, i) => (
        <div key={i} className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3" data-testid={`rp-dest-${i}`}>
          <Campo label="Nombre de la persona o entidad" error={err(`dest-nombre-${i}`)}>
            <input data-testid={`rp-dest-nombre-${i}`} className={inputCls} value={d.nombre} placeholder="Ana" onChange={(e) => setD(i, "nombre", e.target.value)} />
          </Campo>
          <Campo label="Correo electrónico" error={err(`dest-email-${i}`)}>
            <input data-testid={`rp-dest-email-${i}`} type="email" inputMode="email" className={inputCls} value={d.email} placeholder="ana@gmail.com" onChange={(e) => setD(i, "email", e.target.value)} />
          </Campo>
          {f.importeDistinto && (
            <Campo label={`Importe de ${d.nombre || "esta persona"}`} error={err(`dest-importe-${i}`)}>
              <input data-testid={`rp-dest-importe-${i}`} type="number" inputMode="decimal" min="0" className={inputCls} value={d.importe} placeholder="0" onChange={(e) => setD(i, "importe", e.target.value)} />
            </Campo>
          )}
          {f.destinatarios.length > 1 && <button type="button" data-testid={`rp-dest-quitar-${i}`} onClick={() => set({ destinatarios: f.destinatarios.filter((_, j) => j !== i) })} className="min-h-[44px] text-sm font-semibold text-[#B91C1C]">Quitar a esta persona</button>}
        </div>
      ))}
      {err("dest") && <p className="text-sm font-semibold text-[#B91C1C]" role="alert">{err("dest")}</p>}
      {hayDuplicado && <p data-testid="rp-dest-repetido" role="alert" className="text-base font-semibold text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-xl px-3.5 py-2.5">Este contacto ya está añadido como destinatario.</p>}
      <div className="flex flex-col sm:flex-row gap-2">
        {conEmail.length > 0 && (
          <select data-testid="rp-dest-contacto" value="" className={inputCls}
            onChange={(e) => { const c = conEmail.find((x) => x.id === e.target.value); if (c) elegirContacto(c); }}>
            <option value="">Elegir de mis contactos…</option>
            {conEmail.map((c) => <option key={c.id} value={c.id}>{c.nombre} · {c.email}</option>)}
          </select>
        )}
        <button type="button" data-testid="rp-dest-anadir" onClick={() => anadir({ nombre: "", email: "", importe: "" })} className="min-h-[52px] px-4 rounded-xl border-2 border-dashed border-[#C9D8CF] text-base font-semibold text-[#3A6B56] whitespace-nowrap">+ Añadir otra persona</button>
      </div>
      <label className="flex items-start gap-3 min-h-[48px] cursor-pointer" data-testid="rp-importe-distinto">
        <input type="checkbox" className="w-6 h-6 mt-0.5 accent-[#3A6B56]" checked={f.importeDistinto} onChange={(e) => set({ importeDistinto: e.target.checked })} />
        <span className="text-base text-[#1C2421]"><strong>Cada persona paga un importe distinto</strong>
          <span className="block text-sm text-[#6E7A74]">{f.importeDistinto ? "Se usará el importe de cada persona en su correo." : f.importe !== "" ? `Si no lo marcas, todos los correos indicarán ${importeTxt(parseImporte(f.importe))} (el importe del paso 1).` : "Márcalo solo si cada persona tiene una cantidad diferente."}</span></span>
      </label>
      {err("importe-general") && <p className="text-sm font-semibold text-[#B91C1C]" role="alert">{err("importe-general")}</p>}
      <MensajeCorreo f={f} set={set} />
    </div>
  );
};

export const PasoQuien = ({ f, set, error }) => {
  const err = (c) => (error?.campo === c ? error.txt : null);
  const para = (v) => set({ para: v, personas: v === "varias" ? (f.personas.length >= 2 ? f.personas : [f.personas[0] || "", ""]) : [f.personas[0] || ""] });
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Opcion testid="rp-para-yo" sel={f.para === "yo"} onClick={() => para("yo")} titulo="Solo para mí" />
        <Opcion testid="rp-para-otra" sel={f.para === "otra"} onClick={() => para("otra")} titulo="Para otra persona" />
        <Opcion testid="rp-para-varias" sel={f.para === "varias"} onClick={() => para("varias")} titulo="Para varias personas" />
      </div>
      {f.para !== "yo" && <Personas f={f} set={set} err={err} />}
      <Campo label="Relacionar con" opcional ayuda="Persona, vivienda, vehículo o mascota a la que corresponde." testid="rp-campo-relacion">
        <Relacionar value={f.rel} onChange={(v) => set({ rel: v })} testid="rp-relacion" />
      </Campo>
      {f.modo === "correo" && <Destinatarios f={f} set={set} err={err} />}
    </div>
  );
};
